import { Close, Fullscreen, ViewColumn } from '@mui/icons-material';
import {
  Box,
  Checkbox,
  Dialog,
  FormControlLabel,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  lighten,
  useTheme,
} from '@mui/material';
import {
  MRT_ColumnDef,
  MRT_PaginationState,
  MRT_SortingState,
  MRT_VisibilityState,
} from 'material-react-table';
import { useEffect, useMemo, useState } from 'react';
import { UtilityTypeBadge } from 'switch-fe-shared/components/Chips/UtilityBadge';
import SwitchTable from 'switch-fe-shared/components/Tables/SwitchTable';
import useRememberTable from 'switch-fe-shared/hooks/useRememberTable';
import { capitalize, formatNumber } from 'switch-fe-shared/utils/formatting';
import {
  UtilityType,
  UtilityTypeOrAll,
  convertToAndFromBase,
} from 'switch-shared/modules/crud/utility/utility.enums';
import { DurationUnit } from 'switch-shared/utils/duration.enum';

import { AggregateP1SplitGroupBy } from '../../../../../switch-shared/src/modules/crud/utility-data/utility-data.dto';
import { useAggregateReadingsByApplicationQuery } from '../../../services/switch-rest-api/utility-data.service';
import { getUtilityUnits } from '../../../utils/formatters';
import {
  isAllUtilitySelection,
  isUtilityType,
  toScopedId,
} from '../../../utils/selection';
import { Metrics } from '../../dashboard/components/widgets/UtilityUsageWidget';

interface Props {
  handleMetrics: (metrics: Metrics, priorMetrics?: Metrics) => void;
  appGroupId?: string;
  utility: UtilityTypeOrAll;
  startDate: Date;
  endDate: Date;
  bucketSize: number;
  bucketUnit: DurationUnit;
  zoneId?: string;
  onActionsReady?: (actions: React.ReactNode) => void;
  pageSize?: number;
  includePriorPeriod?: boolean;
  onLoadingChange?: (isLoading: boolean) => void;
}

interface TableRow {
  _id: string;
  applicationId: string;
  applicationName: string;
  utilityType?: string;
  bulkUsage: number;
  subMeterUsage: number;
  unmeteredUsage: number;
  totalUsage: number;
  subRows?: TableRow[];
}

const GroupUsageTable = ({
  handleMetrics,
  appGroupId,
  utility,
  startDate,
  endDate,
  bucketSize,
  bucketUnit,
  zoneId,
  onActionsReady,
  pageSize = 5,
  includePriorPeriod = false,
  onLoadingChange,
}: Props) => {
  /* Hooks & Use Effects */
  const theme = useTheme();
  const isUtilitySelected = isUtilityType(utility);

  const [columnVisibility, setColumnVisibility] = useState<MRT_VisibilityState>(
    {}
  );
  const { pagination, setPagination, sorting, setSorting } = useRememberTable({
    initialPageSize: pageSize,
  });
  const [columnMenuAnchorEl, setColumnMenuAnchorEl] =
    useState<null | HTMLElement>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const FULLSCREEN_PAGE_SIZE = 100;
  const [fullscreenPagination, setFullscreenPagination] =
    useState<MRT_PaginationState>({
      pageIndex: 0,
      pageSize: FULLSCREEN_PAGE_SIZE,
    });

  const columnMenuOpen = Boolean(columnMenuAnchorEl);

  const unit = useMemo(() => {
    return getUtilityUnits(utility, true);
  }, [utility]);

  /* States */

  /* API Calls */

  const {
    data: utilityData,
    isLoading: isLoadingUtilityData,
    isFetching: isFetchingUtilityData,
  } = useAggregateReadingsByApplicationQuery(
    {
      startTimeISO: startDate.toISOString(),
      endTimeISO: endDate.toISOString(),
      applicationGroupId: toScopedId(appGroupId),
      utilityType: isUtilitySelected ? utility : undefined,
      bucketSize,
      bucketUnit,
      zoneId,
      groupBy: 'application' as AggregateP1SplitGroupBy,
      includePriorPeriod: false,
    },
    { pollingInterval: 300_000, skip: !toScopedId(appGroupId) }
  );

  const isTableLoading = isLoadingUtilityData;

  useEffect(() => {
    if (onLoadingChange) {
      onLoadingChange(isTableLoading);
      return () => {
        onLoadingChange(false);
      };
    }
    return undefined;
  }, [isTableLoading, onLoadingChange]);

  /* Functions & Functional Components */

  const tableData = useMemo<TableRow[]>(() => {
    if (!utilityData?.buckets) return [];

    if (isAllUtilitySelection(utility)) {
      // Group by application with utility types as sub-rows
      return utilityData.buckets
        .filter((app) => app.applicationId)
        .map((app): TableRow => {
          const subRows: TableRow[] = [];

          // Add utility sub-rows from byUtilityType
          if (app.byUtilityType) {
            const utilityTypes = [
              UtilityType.ELECTRICITY,
              UtilityType.WATER,
              UtilityType.GAS,
            ];

            utilityTypes.forEach((ut) => {
              const utData = app.byUtilityType?.[ut];
              if (utData) {
                const bulkUsage =
                  convertToAndFromBase(utData.deltaP1Bulk, ut as any, true) ??
                  0;
                const subMeterUsage =
                  convertToAndFromBase(
                    utData.deltaP1SubMeter,
                    ut as any,
                    true
                  ) ?? 0;
                const unmeteredUsage =
                  convertToAndFromBase(
                    utData.deltaP1Unmetered,
                    ut as any,
                    true
                  ) ?? 0;

                subRows.push({
                  _id: `${app.applicationId}-${ut}`,
                  applicationId: app.applicationId,
                  applicationName: '',
                  utilityType: ut,
                  bulkUsage,
                  subMeterUsage,
                  unmeteredUsage,
                  totalUsage: bulkUsage + subMeterUsage + unmeteredUsage,
                });
              }
            });
          }

          // Parent row totals (sum from sub-rows)
          const bulkUsage = subRows.reduce((sum, r) => sum + r.bulkUsage, 0);
          const subMeterUsage = subRows.reduce(
            (sum, r) => sum + r.subMeterUsage,
            0
          );
          const unmeteredUsage = subRows.reduce(
            (sum, r) => sum + r.unmeteredUsage,
            0
          );

          return {
            _id: app.applicationId,
            applicationId: app.applicationId,
            applicationName: app.name,
            bulkUsage,
            subMeterUsage,
            unmeteredUsage,
            totalUsage: bulkUsage + subMeterUsage + unmeteredUsage,
            subRows: subRows.length > 0 ? subRows : undefined,
          };
        })
        .filter((row) => row.subRows && row.subRows.length > 0);
    }

    // Single utility - no grouping
    return utilityData.buckets
      .filter((app) => app.applicationId)
      .map((app): TableRow => {
        const bulkUsage =
          convertToAndFromBase(app.deltaP1Bulk, utility, true) ?? 0;
        const subMeterUsage =
          convertToAndFromBase(app.deltaP1SubMeter, utility, true) ?? 0;
        const unmeteredUsage =
          convertToAndFromBase(app.deltaP1Unmetered, utility, true) ?? 0;

        return {
          _id: app.applicationId,
          applicationId: app.applicationId,
          applicationName: app.name,
          bulkUsage,
          subMeterUsage,
          unmeteredUsage,
          totalUsage: bulkUsage + subMeterUsage + unmeteredUsage,
        };
      });
  }, [utilityData, utility]);

  const columns = useMemo<MRT_ColumnDef<TableRow>[]>(() => {
    const baseColumns: MRT_ColumnDef<TableRow>[] = [
      {
        accessorKey: 'applicationName',
        header: 'Application',
        size: 260,
      },
    ];

    // Add Utility column if utility is the "all" selection
    if (isAllUtilitySelection(utility)) {
      baseColumns.push({
        accessorKey: 'utilityType',
        header: 'Utility',
        size: 150,
        Cell: ({ cell }) => {
          const utilityType = cell.getValue<string>();
          if (!utilityType) return null; // Parent rows don't show utility
          return (
            <Box display='flex' justifyContent='flex-start'>
              <UtilityTypeBadge
                type={utilityType as UtilityType}
                size='small'
              />
            </Box>
          );
        },
      });
    }

    baseColumns.push(
      {
        accessorKey: 'bulkUsage',
        header: `Bulk (${unit})`,
        Header: ({ column }) => (
          <Tooltip
            placement='top'
            title={`Consumption from meters connected to BULK meterpoints`}
          >
            <Typography fontWeight={'bold'}>
              Bulk {unit !== 'Units' ? ` (${unit})` : ''}
            </Typography>
          </Tooltip>
        ),
        size: 150,
        Cell: ({ cell, row }) => {
          const value = cell.getValue<number>();
          // Only show unit for leaf rows (utility sub-rows), not parent rows
          if (row.original.utilityType) {
            const utilityUnit = getUtilityUnits(
              row.original.utilityType as UtilityTypeOrAll,
              true
            );
            return `${formatNumber(value)} ${utilityUnit}`;
          }
          return unit !== 'Units' ? formatNumber(value) : '';
        },
      },
      {
        accessorKey: 'subMeterUsage',
        header: `Sub-Meter (${unit})`,
        Header: ({ column }) => (
          <Tooltip
            placement='top'
            title={`Consumption from meters connected to NON-BULK meterpoints`}
          >
            <Typography fontWeight={'bold'}>
              Sub-Meter{unit !== 'Units' ? ` (${unit})` : ''}
            </Typography>
          </Tooltip>
        ),
        size: 150,
        Cell: ({ cell, row }) => {
          const value = cell.getValue<number>();
          // Only show unit for leaf rows (utility sub-rows), not parent rows
          if (row.original.utilityType) {
            const utilityUnit = getUtilityUnits(
              row.original.utilityType as UtilityTypeOrAll,
              true
            );
            return `${formatNumber(value)} ${utilityUnit}`;
          }
          return unit !== 'Units' ? formatNumber(value) : '';
        },
      },
      {
        accessorKey: 'unmeteredUsage',
        header: `Unmetered (${unit})`,
        Header: ({ column }) => (
          <Tooltip
            placement='top'
            title={`The difference in usage between BULK and NON-BULK meterpoint consumption. Not applicable if there are no installed bulk meters`}
          >
            <Typography fontWeight={'bold'}>
              Unmetered {unit !== 'Units' ? ` (${unit})` : ''}
            </Typography>
          </Tooltip>
        ),
        size: 150,
        Cell: ({ cell, row }) => {
          const value = cell.getValue<number>();
          // Only show unit for leaf rows (utility sub-rows), not parent rows
          if (row.original.utilityType) {
            const utilityUnit = getUtilityUnits(
              row.original.utilityType as UtilityTypeOrAll,
              true
            );
            return `${formatNumber(value)} ${utilityUnit}`;
          }
          return unit !== 'Units' ? formatNumber(value) : '';
        },
      },
      {
        accessorKey: 'totalUsage',
        header: `Total (${unit})`,
        Header: ({ column }) => (
          <Tooltip
            placement='top'
            title={`The sum of the BULK, NON-BULK and UNMETERED usage.`}
          >
            <Typography fontWeight={'bold'}>
              Total {unit !== 'Units' ? ` (${unit})` : ''}
            </Typography>
          </Tooltip>
        ),
        size: 150,
        Cell: ({ cell, row }) => {
          const value = cell.getValue<number>();
          // Only show unit for leaf rows (utility sub-rows), not parent rows
          if (row.original.utilityType) {
            const utilityUnit = getUtilityUnits(
              row.original.utilityType as UtilityTypeOrAll,
              true
            );
            return `${formatNumber(value)} ${utilityUnit}`;
          }
          return unit !== 'Units' ? formatNumber(value) : '';
        },
      }
    );

    return baseColumns;
  }, [unit, utility]);

  const columnOrder = useMemo(() => {
    const order = columns.map((column) => column.accessorKey as string);

    if (isAllUtilitySelection(utility)) {
      return ['mrt-row-expand', ...order];
    }

    return order;
  }, [columns, utility]);

  const handleColumnMenuClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    setColumnMenuAnchorEl(event.currentTarget);
  };

  const handleColumnMenuClose = () => {
    setColumnMenuAnchorEl(null);
  };

  const handleColumnToggle = (columnId: string) => {
    setColumnVisibility((prev) => {
      const currentValue = prev[columnId as keyof typeof prev];
      const newValue = currentValue === false ? true : false;
      return {
        ...prev,
        [columnId]: newValue,
      };
    });
  };

  const columnIds = useMemo(
    () =>
      columns.map((col) => ({
        id: col.accessorKey as string,
        header:
          typeof col.header === 'string'
            ? col.header
            : (col.accessorKey as string),
      })),
    [columns]
  );

  const actions = useMemo(
    () => (
      <>
        <Tooltip
          key='columns-tooltip'
          placement='bottom'
          title='Show/Hide columns'
        >
          <IconButton
            key='columns'
            aria-controls={columnMenuOpen ? 'column-menu' : undefined}
            aria-haspopup='true'
            aria-expanded={columnMenuOpen ? 'true' : undefined}
            onClick={handleColumnMenuClick}
            size='small'
          >
            <ViewColumn />
          </IconButton>
        </Tooltip>
        <Menu
          key='column-menu'
          id='column-menu'
          anchorEl={columnMenuAnchorEl}
          open={columnMenuOpen}
          onClose={handleColumnMenuClose}
          MenuListProps={{
            'aria-labelledby': 'column-button',
          }}
        >
          {columnIds.map((column) => (
            <MenuItem
              key={column.id}
              sx={{ height: theme.tokens.spacing.xl * 1.3 }}
              onClick={() => handleColumnToggle(column.id)}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={
                      columnVisibility[
                        column.id as keyof typeof columnVisibility
                      ] !== false
                    }
                  />
                }
                label={column.header}
                sx={{ pointerEvents: 'none', width: '100%' }}
              />
            </MenuItem>
          ))}
        </Menu>
        <Tooltip key='fullscreen-tooltip' title='Fullscreen'>
          <IconButton
            key='fullscreen'
            onClick={(e) => {
              e.stopPropagation();
              setIsFullscreen(true);
            }}
            size='small'
          >
            <Fullscreen />
          </IconButton>
        </Tooltip>
      </>
    ),
    [
      columnMenuOpen,
      columnMenuAnchorEl,
      columnIds,
      columnVisibility,
      theme.tokens.spacing.xl,
    ]
  );

  // Pass actions to parent component and refresh when menu state changes.
  useEffect(() => {
    if (onActionsReady) {
      onActionsReady(actions);
    }
  }, [actions, onActionsReady]);

  const tableComponent = (isFullscreenMode: boolean) => (
    <SwitchTable
      title='applications'
      columns={columns}
      data={tableData}
      muiTableContainerProps={{
        sx: {
          borderRadius: 0,
          minHeight: 0,
          flexGrow: 1,
          display: 'flex',
        },
      }}
      muiTablePaperProps={{
        sx: {
          borderRadius: 0,
          maxHeight: isFullscreenMode ? '100%' : '0px',
        },
      }}
      muiPaginationProps={{
        rowsPerPageOptions: [5, 10, 15, 20, 50, 100],
      }}
      isLoading={isLoadingUtilityData}
      isFetching={isFetchingUtilityData}
      enableRowActions={false}
      enableColumnFilters={false}
      enableSorting={true}
      enablePagination={true}
      enableBottomToolbar={true}
      enableTopToolbar={false}
      enableExpanding={isAllUtilitySelection(utility)}
      enableExpandAll={isAllUtilitySelection(utility)}
      getSubRows={(row) => row.subRows}
      state={{
        pagination: isFullscreenMode ? fullscreenPagination : pagination,
        sorting,
        columnVisibility,
        columnOrder,
      }}
      initialState={{
        pagination: isFullscreenMode ? fullscreenPagination : pagination,
        density: 'compact',
        expanded: true,
      }}
      onPaginationChange={
        isFullscreenMode ? setFullscreenPagination : setPagination
      }
      onSortingChange={setSorting}
      onColumnVisibilityChange={setColumnVisibility}
    />
  );

  useEffect(() => {
    if (!utilityData) return;

    if (isAllUtilitySelection(utility)) {
      // Build per-utility breakdown from totalByUtilityType
      const perUtility: Record<
        string,
        {
          bulk: number;
          subMeter: number;
          communal: number;
          priorBulk?: number;
          priorSubMeter?: number;
          priorCommunal?: number;
        }
      > = {};

      const utilityTypes = [
        UtilityType.ELECTRICITY,
        UtilityType.WATER,
        UtilityType.GAS,
      ];

      utilityTypes.forEach((ut) => {
        const utData = utilityData.totalByUtilityType?.[ut];
        if (utData) {
          perUtility[ut] = {
            bulk:
              convertToAndFromBase(utData.deltaP1Bulk, ut as any, true) || 0,
            subMeter:
              convertToAndFromBase(utData.deltaP1SubMeter, ut as any, true) ||
              0,
            communal:
              convertToAndFromBase(utData.deltaP1Unmetered, ut as any, true) ||
              0,
          };

          const priorUtData = utilityData.priorTotalByUtilityType?.[ut];
          if (priorUtData) {
            perUtility[ut].priorBulk =
              convertToAndFromBase(priorUtData.deltaP1Bulk, ut as any, true) ||
              0;
            perUtility[ut].priorSubMeter =
              convertToAndFromBase(
                priorUtData.deltaP1SubMeter,
                ut as any,
                true
              ) || 0;
            perUtility[ut].priorCommunal =
              convertToAndFromBase(
                priorUtData.deltaP1Unmetered,
                ut as any,
                true
              ) || 0;
          }
        }
      });

      const currentMetrics: Metrics = {
        bulk: utilityData.totalDeltaP1Bulk || 0,
        subMeter: utilityData.totalDeltaP1SubMeter || 0,
        communal: utilityData.totalDeltaP1Unmetered || 0,
        perUtility,
      };

      handleMetrics(currentMetrics, undefined);
    } else {
      // Single utility
      const currentMetrics = {
        bulk:
          convertToAndFromBase(utilityData.totalDeltaP1Bulk, utility, true) ??
          0,
        subMeter:
          convertToAndFromBase(
            utilityData.totalDeltaP1SubMeter,
            utility,
            true
          ) ?? 0,
        communal:
          convertToAndFromBase(
            utilityData.totalDeltaP1Unmetered,
            utility,
            true
          ) ?? 0,
      };

      const priorMetrics =
        utilityData.priorTotalDeltaP1Bulk !== undefined
          ? {
              bulk:
                convertToAndFromBase(
                  utilityData.priorTotalDeltaP1Bulk ?? 0,
                  utility,
                  true
                ) ?? 0,
              subMeter:
                convertToAndFromBase(
                  utilityData.priorTotalDeltaP1SubMeter ?? 0,
                  utility,
                  true
                ) ?? 0,
              communal:
                convertToAndFromBase(
                  utilityData.priorTotalDeltaP1Unmetered ?? 0,
                  utility,
                  true
                ) ?? 0,
            }
          : undefined;

      handleMetrics(currentMetrics, priorMetrics);
    }
  }, [utilityData, utility, handleMetrics]);

  return (
    <>
      {tableComponent(false)}

      {/* Fullscreen Modal */}
      <Dialog
        open={isFullscreen}
        onClose={() => setIsFullscreen(false)}
        fullScreen
        PaperProps={{
          sx: {
            background: theme.colors.neutral.grey[20],
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100vh',
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: { xs: 2, md: 3 },
              py: { xs: 1.5, md: 2 },
              borderBottom: `1px solid ${theme.tokens.colors.border}`,
              background: lighten(theme.palette.primary.main, 0.95),
            }}
          >
            <Box>
              <Typography
                variant='h1'
                fontSize={{
                  xs: theme.tokens.typography.fontSize.lg,
                  md: theme.tokens.typography.fontSize.xl,
                }}
                fontFamily={theme.tokens.typography.fontFamily.display}
                color={theme.palette.primary.main}
                fontWeight={theme.tokens.typography.fontWeight.semibold}
              >
                {isAllUtilitySelection(utility)
                  ? 'Combined Utility'
                  : capitalize(utility)}{' '}
                Usage
              </Typography>
              <Typography
                variant='body2'
                sx={{ color: theme.tokens.colors.mutedForeground }}
              >
                Usage breakdown by application
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title='Show/Hide columns'>
                <IconButton onClick={handleColumnMenuClick}>
                  <ViewColumn />
                </IconButton>
              </Tooltip>
              <Tooltip title='Close fullscreen'>
                <IconButton onClick={() => setIsFullscreen(false)}>
                  <Close />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* Table */}
          <Box
            sx={{
              flex: 1,
              overflow: 'auto',
            }}
          >
            {tableComponent(true)}
          </Box>
        </Box>
      </Dialog>
    </>
  );
};

export default GroupUsageTable;
